import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  List,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  Divider,
  Pagination,
} from "@mui/material";
import { Check, Close, HelpOutline, SearchOff } from "@mui/icons-material";
import Header from "../../components/Header";
import {
  useGetReviewQuery,
  useGetMyDecisionsQuery,
  useSubmitDecisionMutation,
} from "../../services/review.service";

const VALUE_META = {
  include: { label: "Include", color: "success", icon: <Check fontSize="small" /> },
  exclude: { label: "Exclude", color: "error", icon: <Close fontSize="small" /> },
  maybe: { label: "Maybe", color: "warning", icon: <HelpOutline fontSize="small" /> },
  not_retrieved: { label: "Not retrieved", color: "default", icon: <SearchOff fontSize="small" /> },
};

const PAGE_SIZE = 20;

const DecisionRow = ({ reviewId, stage, decision, locked, exclusionReasons }) => {
  const [submitDecision, { isLoading }] = useSubmitDecisionMutation();
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeReason, setExcludeReason] = useState(decision.reason || "");
  const [saved, setSaved] = useState(false);

  const record = decision.record;
  const meta = VALUE_META[decision.value] || { label: decision.value, color: "default" };

  const change = async (value, extra = {}) => {
    await submitDecision({ reviewId, stage, recordId: record._id, value, ...extra }).unwrap();
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const triggerExclude = () => {
    if (stage === 3) {
      setExcludeReason("");
      setExcludeDialogOpen(true);
    } else {
      change("exclude");
    }
  };

  const confirmExclude = () => {
    if (!excludeReason) return;
    setExcludeDialogOpen(false);
    change("exclude", { reason: excludeReason });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="body1">{record?.title || "(record removed)"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[record?.journal, record?.year].filter(Boolean).join(" · ")}
            {record?.pmid ? ` · PMID ${record.pmid}` : ""}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={decision.reason ? `${meta.label} — ${decision.reason}` : meta.label}
            color={meta.color}
            size="small"
          />
          {saved && <Chip label="Saved" color="success" size="small" variant="outlined" />}
        </Box>
      </Box>

      {!locked && record && (
        <Box display="flex" gap={1} mt={1.5}>
          <Button size="small" variant="outlined" color="error" disabled={isLoading} onClick={triggerExclude}>
            Exclude
          </Button>
          <Button size="small" variant="outlined" color="warning" disabled={isLoading} onClick={() => change("maybe")}>
            Maybe
          </Button>
          <Button size="small" variant="outlined" color="success" disabled={isLoading} onClick={() => change("include")}>
            Include
          </Button>
          {stage === 3 && (
            <Button size="small" variant="outlined" disabled={isLoading} onClick={() => change("not_retrieved")}>
              Not retrieved
            </Button>
          )}
        </Box>
      )}

      <Dialog open={excludeDialogOpen} onClose={() => setExcludeDialogOpen(false)} fullWidth>
        <DialogTitle>Exclude — reason required</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Exclusion reason"
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            sx={{ mt: 1 }}
          >
            {exclusionReasons.map((reason) => (
              <MenuItem key={reason} value={reason}>{reason}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcludeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!excludeReason} onClick={confirmExclude}>
            Exclude
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const StagePanel = ({ reviewId, stage, locked, exclusionReasons }) => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const { data, isLoading } = useGetMyDecisionsQuery({ reviewId, stage, page, pageSize: PAGE_SIZE, q: query || undefined });

  const decisions = data?.decisions || [];
  const total = data?.total || 0;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <Box>
      {locked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stage {stage} is locked. The owner must unlock it before decisions here can be changed.
        </Alert>
      )}
      {!locked && stage < 3 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          If a record already has a later-stage decision, changing its Stage {stage} verdict here won't remove that
          later decision automatically — check Stage {stage + 1}{stage === 1 ? " and 3" : ""} for it too if needed.
        </Alert>
      )}
      <TextField
        fullWidth
        size="small"
        label="Search by title, PMID or DOI"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setPage(0); }}
        sx={{ mb: 2 }}
      />
      {isLoading && <Typography>Loading...</Typography>}
      {!isLoading && decisions.length === 0 && (
        <Alert severity="info">No decisions at Stage {stage} {query ? "match that search." : "yet."}</Alert>
      )}
      <List disablePadding>
        {decisions.map((d) => (
          <DecisionRow
            key={d._id}
            reviewId={reviewId}
            stage={stage}
            decision={d}
            locked={locked}
            exclusionReasons={exclusionReasons}
          />
        ))}
      </List>
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(e, p) => setPage(p - 1)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

const MyDecisions = () => {
  const { reviewId } = useParams();
  const { data: reviewData } = useGetReviewQuery(reviewId);
  const [tab, setTab] = useState(0);
  const stage = tab + 1;

  const review = reviewData?.review;

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title="My decisions"
        subtitle="Find a record you've already screened and change its verdict or exclusion reason"
      />
      <Divider sx={{ my: 2 }} />
      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Stage 1: Titles" />
        <Tab label="Stage 2: Titles and abstracts" />
        <Tab label="Stage 3: Full text" />
      </Tabs>
      <Box mt={2}>
        {review && (
          <StagePanel
            reviewId={reviewId}
            stage={stage}
            locked={Boolean(review.stageLocks?.[`stage${stage}`])}
            exclusionReasons={review.exclusionReasons || []}
          />
        )}
      </Box>
    </Box>
  );
};

export default MyDecisions;
